const normalizeProductLineService = (service = {}) => {
  if (!service || typeof service !== 'object' || Array.isArray(service)) {
    return service;
  }

  const normalized = { ...service };

  if (Array.isArray(service.descriptionLines)) {
    normalized.description = service.descriptionLines
      .map(line => (line === null || line === undefined ? '' : String(line)))
      .join('\n');
  } else if (typeof service.description === 'string') {
    normalized.description = service.description;
  } else {
    normalized.description = '';
  }

  return normalized;
};

const normalizeProductLineCatalog = (catalog = {}) => {
  if (!catalog || typeof catalog !== 'object' || Array.isArray(catalog)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(catalog).map(([category, services]) => [
      category,
      Array.isArray(services) ? services.map(normalizeProductLineService) : []
    ])
  );
};

const isProductLineServiceDisabled = (service = {}) => (
  Boolean(service && typeof service === 'object' && !Array.isArray(service) && (
    service.disabled === true
  ))
);

const filterPublicProductLineCatalog = (catalog = {}) => (
  Object.fromEntries(
    Object.entries(normalizeProductLineCatalog(catalog))
      .map(([category, services]) => [
        category,
        services.filter(service => !isProductLineServiceDisabled(service))
      ])
      .filter(([, services]) => services.length > 0)
  )
);

const findProductLineServiceConfig = (catalog = {}, serviceName = '') => {
  if (!serviceName) return null;

  return Object.values(catalog)
    .flatMap(category => (Array.isArray(category) ? category : []))
    .find(item => item?.value === serviceName) || null;
};

const getProductLineServiceValue = (service) => (
  String(typeof service === 'string' ? service : service?.value || '')
);

const getProductLineServiceCode = (service) => (
  getProductLineServiceValue(service).trim().split(/\s+/)[0] || ''
);

const getSelectedServiceValuesByCategory = (serviceItems = [], category = '') => {
  if (!Array.isArray(serviceItems)) return [];

  return serviceItems
    .filter(item => item?.category === category && Array.isArray(item.services))
    .flatMap(item => item.services)
    .map(service => String(service?.service || '').trim())
    .filter(Boolean);
};

const normalizeServiceCodeList = (value) => {
  const values = Array.isArray(value) ? value : [value];

  return values
    .map(item => getProductLineServiceCode(item))
    .filter(Boolean);
};

const getSequencingRestrictedExtractionServiceCodes = (serviceItems = [], catalog = {}) => {
  const codes = new Set();

  getSelectedServiceValuesByCategory(serviceItems, '定序服務 (S)').forEach(serviceName => {
    const serviceConfig = findProductLineServiceConfig(catalog, serviceName);
    normalizeServiceCodeList(serviceConfig?.binding?.extractionServices)
      .forEach(code => codes.add(code));
  });

  return Array.from(codes);
};

const filterExtractionServicesForSequencing = (services = [], serviceItems = [], catalog = {}) => {
  if (!Array.isArray(services)) return [];

  const restrictedCodes = getSequencingRestrictedExtractionServiceCodes(serviceItems, catalog);
  if (restrictedCodes.length === 0) return services;

  const allowedCodes = new Set(restrictedCodes);
  return services.filter(service => allowedCodes.has(getProductLineServiceCode(service)));
};

const sanitizeServiceItemsForPublicCatalog = (serviceItems = [], catalog = {}) => {
  if (!Array.isArray(serviceItems)) return serviceItems;

  const normalizedCatalog = normalizeProductLineCatalog(catalog);
  const availableByCategory = Object.fromEntries(
    Object.entries(normalizedCatalog).map(([category, services]) => [
      category,
      new Set(services.map(getProductLineServiceValue).filter(Boolean))
    ])
  );

  return serviceItems.map(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;

    const category = item.category || '';
    const availableServices = availableByCategory[category];
    if (!availableServices || !Array.isArray(item.services)) return item;

    return {
      ...item,
      services: item.services.map(service => {
        if (!service?.service || availableServices.has(String(service.service))) {
          return service;
        }

        return {
          ...service,
          service: '',
          description: '',
          spec: ''
        };
      })
    };
  });
};

export {
  normalizeProductLineService,
  normalizeProductLineCatalog,
  isProductLineServiceDisabled,
  filterPublicProductLineCatalog,
  sanitizeServiceItemsForPublicCatalog,
  findProductLineServiceConfig,
  filterExtractionServicesForSequencing
};
